<template>
  <QPageContainer>
    <QPage class="recruitment-candidates-list">
      <transition appear :enter-active-class="transition.classes.enter" :leave-active-class="transition.classes.leave">
        <QPullToRefresh no-mouse @refresh="refreshRecruitment">
          <div v-if="!recruitment.isExpired()">
            <CandidateFilterComponent :filters="filtersModel.filters" @apply-filters="applyFilters">
              <template #filters>
                <FiltersComponent
                  :filters="filtersModel.filters"
                  :emit-on-input="$q.screen.gt.xs"
                  @search-location="searchLocation"
                  @apply-filters="applyFilters"
                />
              </template>
            </CandidateFilterComponent>
          </div>
          <div v-if="filtersModel.filters.hasFiltersActive()">
            <p class="text-subtitle2 text-italic q-mt-md q-pl-md">
              {{ $tc("ETAB.PAGES.CANDIDATE.FILTERS.@RESULTS", candidates.length) }}
            </p>
          </div>
          <transition-group tag="div" name="candidate-list">
            <PermanentCandidatesCard
              v-for="candidate in candidates"
              :id="candidate.getId()"
              :key="candidate.getId()"
              :candidate="candidate"
              :recruitment="recruitment"
              :show-experience="true"
              :can-ignore="recruitment.isUnlocked()"
              :can-contact="false"
              @on-contact-candidate="onContactCandidate"
              @ignore-candidate="ignoreCandidate"
              @go-to-profile="goToProfile"
            />
          </transition-group>

          <NoCandidate
            v-if="!recruitment.isExpired() && candidates.length === 0 && !filtersModel.filters.hasFiltersActive()"
            :img="candidateImg"
            :title="$t('ETAB.PAGES.CANDIDATE.@NO_CURRENT_CANDIDATES')"
          />

          <NoFilteredCandidate
            v-if="candidates.length === 0 && filtersModel.filters.hasFiltersActive()"
            :title="$t('ETAB.PAGES.CANDIDATE.FILTERS.@NO_FILTERED_CANDIDATE_TITLE')"
            :subtitle="$t('ETAB.PAGES.CANDIDATE.FILTERS.@NO_FILTERED_CANDIDATE_SUBTITLE')"
            :img="noResultImg"
            @delete-filters="deleteFilters"
          />

          <ExpiredRecruitment v-if="recruitment.isExpired()" />
        </QPullToRefresh>
      </transition>
    </QPage>
    <QPageSticky v-if="$q.screen.gt.xs" class="sticky-over-layout" :offset="recruitment.canBeActivated() ? [10, -50] : [10, 8]">
      <QBtn fab icon="icon-f-rotate-cw" color="secondary" @click="refreshRecruitment" />
    </QPageSticky>
  </QPageContainer>
</template>

<script setup>
  import candidateImg from "@etab/assets/img/placeholders/candidate_current_placeholder.png";
  import noResultImg from "@etab/assets/img/filter-no-results.svg";
  import {
    GETTER_RECRUITMENT_GET_CURRENT,
    ACTION_RECRUITMENT_CANDIDATES_FILTERS_GET_LOCATION_OPTIONS,
    ACTION_RECRUITMENT_CANDIDATES_GET_APPLIED,
    GETTER_RECRUITMENT_CANDIDATES_GET_APPLIED,
    GETTER_RECRUITMENT_CANDIDATES_FILTERS_GET_LANGUAGE_OPTIONS,
    GETTER_RECRUITMENT_CANDIDATES_FILTERS_GET_EXPERIENCE_OPTIONS,
    ACTION_RECRUITMENT_GET,
  } from "@etab/stores/types";
  import { QueryHelpers } from "@core/helpers";
  import { RecruitmentCandidatesFiltersModel } from "@core/models";
  import { NotificationsManager } from "@core/managers/notifications.manager";
  import { useRecruitmentCandidate } from "@etab/composables";
  import { useRecruitmentStore } from "@etab/stores/recruitment";
  import { useRecruitmentCandidatesStore } from "@etab/stores/recruitment-candidates";
  import { useRecruitmentCandidatesFiltersStore } from "@etab/stores/recruitment-candidates-filters";
  import { reactive, computed, onMounted } from "vue";
  import { useRoute, useRouter } from "vue-router";
  import { useTransition } from "@core/composables";
  import { LoadingManager } from "@core/managers/loading.manager";

  import PermanentCandidatesCard from "@etab/components/candidates/PermanentCandidates.card.vue";
  import CandidateFilterComponent from "@etab/components/recruitments/detail/CandidatesFilters.component.vue";
  import FiltersComponent from "@etab/components/recruitments/detail/Filters.component.vue";
  import NoCandidate from "@etab/components/candidates/NoCandidate.functional.vue";
  import NoFilteredCandidate from "@etab/components/candidates/NoFilteredCandidate.component.vue";
  import ExpiredRecruitment from "@etab/components/candidates/ExpiredRecruitment.component.vue";

  const { transition } = useTransition();
  const { $_goToProfileFromPermanentDemand, $_scrollToPreviouslyOpenedExtra, $_ignorePermanentCandidate, $_onContactCandidate } =
    useRecruitmentCandidate();
  const route = useRoute();
  const router = useRouter();

  const recruitmentStore = useRecruitmentStore();
  const recruitmentCandidatesStore = useRecruitmentCandidatesStore();
  const recruitmentCandidatesFiltersStore = useRecruitmentCandidatesFiltersStore();

  const filtersModel = reactive({ filters: new RecruitmentCandidatesFiltersModel() });

  const demandId = computed(() => {
    return route.params.demandId;
  });

  const demandType = computed(() => {
    return route.params.demandType;
  });

  const languageOptions = computed(() => {
    return recruitmentCandidatesFiltersStore[GETTER_RECRUITMENT_CANDIDATES_FILTERS_GET_LANGUAGE_OPTIONS];
  });

  const experienceOptions = computed(() => {
    return recruitmentCandidatesFiltersStore[GETTER_RECRUITMENT_CANDIDATES_FILTERS_GET_EXPERIENCE_OPTIONS];
  });

  const recruitment = computed(() => {
    return recruitmentStore[GETTER_RECRUITMENT_GET_CURRENT];
  });

  const candidates = computed(() => {
    if (recruitment.value.isExpired()) {
      // RG: Recruitment candidates - we don't display candidates for an expired recruitment
      return [];
    }
    return recruitmentCandidatesStore[GETTER_RECRUITMENT_CANDIDATES_GET_APPLIED];
  });

  const searchLocation = function (val) {
    const city = QueryHelpers.mergeQueryParams(route.query.q, val);
    recruitmentCandidatesFiltersStore[ACTION_RECRUITMENT_CANDIDATES_FILTERS_GET_LOCATION_OPTIONS](city);
  };

  const applyFilters = async function (refresherDone) {
    const isFromPullToRefresh = typeof refresherDone === "function";

    try {
      if (!isFromPullToRefresh) {
        LoadingManager.show();
      }

      // we set querys from model to apply filtersModel.filters
      const query = {
        ...filtersModel.filters.getQuery(),
      };
      await Promise.all([
        recruitmentStore[ACTION_RECRUITMENT_GET]({ demandId: demandId.value, demandType: demandType.value }),
        recruitmentCandidatesStore[ACTION_RECRUITMENT_CANDIDATES_GET_APPLIED]({
          demandId: demandId.value,
          type: demandType.value,
          query,
        }),
      ]);
      updateQuery(query);
    } catch (error) {
      NotificationsManager.error();
      throw error;
    } finally {
      LoadingManager.hide();
      if (isFromPullToRefresh) {
        refresherDone();
      }
    }
  };

  const updateQuery = function (query) {
    const params = route.params;
    // we replace in order to avoid cluttering the history
    return router.replace({ name: route.name, query, params }).catch((suppressedError) => {});
  };

  const ignoreCandidate = async function ({ candidate }) {
    try {
      LoadingManager.show();
      await $_ignorePermanentCandidate({ candidate, demandId: demandId.value });
    } catch (error) {
      NotificationsManager.error();
      throw error;
    } finally {
      // stoploading after refresh
      refreshRecruitment();
    }
  };

  const refreshRecruitment = function (refresherDone) {
    applyFilters(refresherDone);
  };

  const goToProfile = function ({ candidate }) {
    $_goToProfileFromPermanentDemand({ candidate });
  };

  const onContactCandidate = async function ({ candidate }) {
    $_onContactCandidate({ candidate });
  };

  const deleteFilters = function () {
    filtersModel.filters = new RecruitmentCandidatesFiltersModel();
    filtersModel.filters.initToggles();
    applyFilters();
  };

  onMounted(() => {
    $_scrollToPreviouslyOpenedExtra();
    filtersModel.filters = new RecruitmentCandidatesFiltersModel();
    filtersModel.filters.initFilters({ query: route.query, languageOptions: languageOptions.value, experienceOptions: experienceOptions.value });
  });
</script>
